import React, { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { Play, Pause, SkipForward, Star, List, Check, Trash2, Bell, X, Settings, ChevronLeft, HelpCircle } from 'lucide-react';
import { unlockAudio } from '../utils/audioUnlock';
import LiveMobileSettingsModal from './LiveMobileSettingsModal';
import '../styles/LiveMobile.css';

/**
 * LiveMobileView - Full-screen broadcast mode for reviewers
 * 
 * IMPORTANT: Actions are FINAL in Live Mobile
 * Once a track is moved via Spot/Done/Del, it cannot be acted upon again.
 * We track acted-upon track IDs to prevent double-counting.
 */
const LiveMobileView = ({
  reviewer,
  queue,
  currentTrack,
  isPlaying,
  playlistMode,
  playlistQueue,
  playlistIndex,
  wsConnected = true,
  setCurrentTrack,
  setIsPlaying,
  setPlaylistMode,
  setPlaylistQueue,
  setPlaylistIndex,
  addToSpotlight,
  moveToFinished,
  moveToRemoved,
  // eslint-disable-next-line no-unused-vars
  startPlaylistMode,
  onClose,
  skipNotification = null,
  children,
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  
  // LOCAL STATE for immediate visual feedback
  const [localIsPlaying, setLocalIsPlaying] = useState(isPlaying);
  const [localTrack, setLocalTrack] = useState(currentTrack);
  const [localPlaylistMode, setLocalPlaylistMode] = useState(playlistMode);
  const [renderKey, setRenderKey] = useState(0);
  
  // Track IDs that have been acted upon (Spot/Done/Del) - CANNOT be acted upon again
  const [actedUponIds, setActedUponIds] = useState(new Set());
  
  // Counters for Done/Del actions
  const [finishedCount, setFinishedCount] = useState(0);
  const [removedCount, setRemovedCount] = useState(0);
  
  // Settings modal state
  const [showSettings, setShowSettings] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [customBackgroundImage, setCustomBackgroundImage] = useState(null);
  const [customBackgroundVideo, setCustomBackgroundVideo] = useState(null);
  const [backgroundsLoaded, setBackgroundsLoaded] = useState(false);
  
  // Long-press state for Next/Restart button
  const longPressTimerRef = useRef(null);
  const [isLongPress, setIsLongPress] = useState(false);
  
  // Fetch background settings on mount - this is the source of truth
  useEffect(() => {
    const fetchBackgroundSettings = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          // Fallback to reviewer prop if no token
          setCustomBackgroundImage(reviewer?.live_mobile_image || null);
          setCustomBackgroundVideo(reviewer?.live_mobile_video || null);
          setBackgroundsLoaded(true);
          return;
        }
        
        const response = await fetch('/api/live-mobile/settings', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('📷 Live Mobile backgrounds loaded:', data);
          // Always set the state, even if null (to clear old values)
          setCustomBackgroundImage(data.live_mobile_image || null);
          setCustomBackgroundVideo(data.live_mobile_video || null);
        } else {
          // Fallback to reviewer prop on API error
          setCustomBackgroundImage(reviewer?.live_mobile_image || null);
          setCustomBackgroundVideo(reviewer?.live_mobile_video || null);
        }
      } catch (error) {
        console.error('Error fetching background settings:', error);
        // Fallback to reviewer prop on fetch error
        setCustomBackgroundImage(reviewer?.live_mobile_image || null);
        setCustomBackgroundVideo(reviewer?.live_mobile_video || null);
      } finally {
        setBackgroundsLoaded(true);
      }
    };
    
    fetchBackgroundSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount
  
  // === SOUNDCLOUD AUTOPLAY FIX ===
  // Two-phase playback initiation to maintain user gesture chain
  // Phase 1: User clicks -> unlockAudio() called synchronously
  // Phase 2: useEffect triggers playback after state is set
  const [pendingPlaylistStart, setPendingPlaylistStart] = useState(null);
  
  // Sync local state FROM parent
  useEffect(() => {
    setLocalIsPlaying(isPlaying);
  }, [isPlaying]);
  
  useEffect(() => {
    setLocalTrack(currentTrack);
  }, [currentTrack]);
  
  useEffect(() => {
    setLocalPlaylistMode(playlistMode);
  }, [playlistMode]);
  
  // === SOUNDCLOUD AUTOPLAY FIX: Phase 2 ===
  // This useEffect triggers AFTER pendingPlaylistStart is set
  // The audio context was already unlocked in Phase 1 (click handler)
  // Now we set the actual track and playing state
  useEffect(() => {
    if (pendingPlaylistStart && pendingPlaylistStart.tracks && pendingPlaylistStart.tracks.length > 0) {
      console.log('[LiveMobile] Phase 2: Starting playlist playback');
      const firstTrack = pendingPlaylistStart.tracks[0];
      
      // Set all playlist state
      flushSync(() => {
        setPlaylistQueue(pendingPlaylistStart.tracks);
        setPlaylistIndex(0);
        setPlaylistMode(true);
        setCurrentTrack(firstTrack);
        setIsPlaying(true);
      });
      
      // Update local state
      setLocalPlaylistMode(true);
      setLocalTrack(firstTrack);
      setLocalIsPlaying(true);
      
      // Clear pending state
      setPendingPlaylistStart(null);
      
      // Force repaint
      if (containerRef.current) {
        containerRef.current.style.opacity = '0.999';
        void containerRef.current.offsetHeight;
        containerRef.current.style.opacity = '1';
      }
      setRenderKey(k => k + 1);
    }
  }, [pendingPlaylistStart, setPlaylistQueue, setPlaylistIndex, setPlaylistMode, setCurrentTrack, setIsPlaying]);
  
  // Get tracks from queue, EXCLUDING acted-upon tracks
  const upcomingTracks = (queue?.upcoming || []).filter(t => !actedUponIds.has(t.id));
  const spotlightTracks = (queue?.spotlight || []).filter(t => !actedUponIds.has(t.id));
  
  // Active track - must NOT be an acted-upon track
  const getActiveTrack = useCallback(() => {
    // If we have a local track and it hasn't been acted upon, use it
    if (localTrack && !actedUponIds.has(localTrack.id)) {
      return localTrack;
    }
    // Otherwise get first non-acted-upon track from upcoming
    return upcomingTracks[0] || null;
  }, [localTrack, actedUponIds, upcomingTracks]);
  
  const activeTrack = getActiveTrack();
  
  // Force repaint helper
  const forceRepaint = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.style.opacity = '0.999';
      void containerRef.current.offsetHeight;
      containerRef.current.style.opacity = '1';
    }
    setRenderKey(k => k + 1);
  }, []);
  
  // Count submissions (for badges) - excluding acted-upon
  const upcomingCount = upcomingTracks.length;
  const paidCount = upcomingTracks.filter(t => 
    ['skip', 'superskip', 'custom'].includes(t.submission_type)
  ).length;
  
  // Background video loop
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.loop = true;
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => {});
    }
  }, [reviewer?.live_mobile_video]);
  
  // Helper: Get next track from Upcoming queue (excluding acted-upon)
  const getNextUpcomingTrack = useCallback(() => {
    // Get tracks that haven't been acted upon
    const availableTracks = upcomingTracks.filter(t => t.id !== activeTrack?.id);
    return availableTracks[0] || null;
  }, [upcomingTracks, activeTrack]);
  
  // Helper: Get next track in Spotlight/List mode
  const getNextPlaylistTrack = useCallback(() => {
    if (!playlistQueue || playlistQueue.length === 0) return null;
    
    // Find current track in playlist and get next one
    const currentIndex = playlistQueue.findIndex(t => t.id === activeTrack?.id);
    if (currentIndex === -1) {
      // Current track not in playlist, return first
      return playlistQueue[0] || null;
    }
    
    // Return next track if available
    const nextIndex = currentIndex + 1;
    if (nextIndex < playlistQueue.length) {
      return playlistQueue[nextIndex];
    }
    
    return null; // End of playlist
  }, [playlistQueue, activeTrack]);
  
  // Helper: Mark track as acted upon and clear it
  const markAsActedUpon = useCallback((trackId) => {
    if (!trackId) return;
    
    console.log('[LiveMobile] Marking track as acted upon:', trackId);
    
    // Add to acted-upon set
    setActedUponIds(prev => new Set([...prev, trackId]));
    
    // Immediately stop and clear if this is the current track
    setLocalIsPlaying(false);
    setLocalTrack(null);
    
    flushSync(() => {
      setCurrentTrack(null);
      setIsPlaying(false);
    });
  }, [setCurrentTrack, setIsPlaying]);
  
  // Helper: Advance to next available track
  const advanceToNextTrack = useCallback(() => {
    const nextTrack = getNextUpcomingTrack();
    
    if (nextTrack) {
      console.log('[LiveMobile] Advancing to next track:', nextTrack.title);
      unlockAudio();
      setLocalTrack(nextTrack);
      setLocalIsPlaying(true);
      
      flushSync(() => {
        setCurrentTrack(nextTrack);
        setIsPlaying(true);
      });
    } else {
      console.log('[LiveMobile] No more tracks available');
      setLocalTrack(null);
      setLocalIsPlaying(false);
    }
    
    forceRepaint();
  }, [getNextUpcomingTrack, setCurrentTrack, setIsPlaying, forceRepaint]);
  
  // ============================================================
  // PLAY BUTTON - with counter, returns to Upcoming if in List mode
  // ============================================================
  const handlePlay = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('[LiveMobile] Play tapped, localIsPlaying:', localIsPlaying, 'playlistMode:', localPlaylistMode);
    
    unlockAudio();
    
    if (localIsPlaying) {
      // Currently playing - PAUSE
      if (localPlaylistMode) {
        // Exit List mode and return to Upcoming
        console.log('[LiveMobile] Exiting List mode, returning to Upcoming');
        setLocalIsPlaying(false);
        setLocalPlaylistMode(false);
        
        const nextUpcoming = getNextUpcomingTrack();
        if (nextUpcoming) {
          setLocalTrack(nextUpcoming);
        }
        
        flushSync(() => {
          setIsPlaying(false);
          setPlaylistMode(false);
          setPlaylistQueue([]);
          setPlaylistIndex(0);
          if (nextUpcoming) {
            setCurrentTrack(nextUpcoming);
          }
        });
      } else {
        // Normal pause
        setLocalIsPlaying(false);
        flushSync(() => {
          setIsPlaying(false);
        });
      }
      forceRepaint();
    } else {
      // Not playing - START from Upcoming queue
      const trackToPlay = activeTrack || upcomingTracks[0];
      
      if (trackToPlay && !actedUponIds.has(trackToPlay.id)) {
        console.log('[LiveMobile] Starting playback:', trackToPlay.title);
        setLocalIsPlaying(true);
        setLocalTrack(trackToPlay);
        setLocalPlaylistMode(false);
        
        flushSync(() => {
          setCurrentTrack(trackToPlay);
          setIsPlaying(true);
          setPlaylistMode(false);
        });
        forceRepaint();
      }
    }
  }, [localIsPlaying, localPlaylistMode, activeTrack, upcomingTracks, actedUponIds, getNextUpcomingTrack,
      setIsPlaying, setCurrentTrack, setPlaylistMode, setPlaylistQueue, setPlaylistIndex, forceRepaint]);
  
  // ============================================================
  // NEXT BUTTON (tap) - Skip to next track
  // RESTART (long-press) - Restart current track
  // ============================================================
  
  // Handle NEXT - advance to next track (single tap)
  // Works in both Upcoming mode and List/Spotlight mode
  const handleNext = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('[LiveMobile] Next tapped, playlistMode:', localPlaylistMode);
    unlockAudio();
    
    let nextTrack = null;
    
    if (localPlaylistMode) {
      // In List/Spotlight mode - get next from playlist queue
      nextTrack = getNextPlaylistTrack();
      console.log('[LiveMobile] Next in playlist:', nextTrack?.title || 'none');
      
      if (nextTrack) {
        // Update playlist index
        const newIndex = playlistQueue.findIndex(t => t.id === nextTrack.id);
        
        setLocalTrack(nextTrack);
        setLocalIsPlaying(true);
        
        flushSync(() => {
          setCurrentTrack(nextTrack);
          setIsPlaying(true);
          setPlaylistIndex(newIndex >= 0 ? newIndex : playlistIndex + 1);
        });
        forceRepaint();
      } else {
        // End of playlist
        console.log('[LiveMobile] End of playlist reached');
        setLocalTrack(null);
        setLocalIsPlaying(false);
        setLocalPlaylistMode(false);
        
        flushSync(() => {
          setCurrentTrack(null);
          setIsPlaying(false);
          setPlaylistMode(false);
          setPlaylistQueue([]);
          setPlaylistIndex(0);
        });
      }
    } else {
      // In Upcoming mode - get next from upcoming queue
      nextTrack = getNextUpcomingTrack();
      console.log('[LiveMobile] Next in upcoming:', nextTrack?.title || 'none');
      
      if (nextTrack) {
        setLocalTrack(nextTrack);
        setLocalIsPlaying(true);
        
        flushSync(() => {
          setCurrentTrack(nextTrack);
          setIsPlaying(true);
        });
        forceRepaint();
      } else {
        console.log('[LiveMobile] No more upcoming tracks');
        setLocalTrack(null);
        setLocalIsPlaying(false);
        
        flushSync(() => {
          setCurrentTrack(null);
          setIsPlaying(false);
        });
      }
    }
  }, [localPlaylistMode, playlistQueue, playlistIndex, getNextPlaylistTrack, getNextUpcomingTrack, 
      setCurrentTrack, setIsPlaying, setPlaylistMode, setPlaylistQueue, setPlaylistIndex, forceRepaint]);
  
  // Handle RESTART - restart current track (long-press)
  const handleRestart = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!activeTrack || actedUponIds.has(activeTrack.id)) return;
    
    console.log('[LiveMobile] Restart (long-press) triggered');
    unlockAudio();
    
    const trackToRestart = activeTrack;
    setLocalTrack(null);
    setLocalIsPlaying(false);
    
    flushSync(() => {
      setCurrentTrack(null);
      setIsPlaying(false);
    });
    
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setLocalTrack(trackToRestart);
        setLocalIsPlaying(true);
        flushSync(() => {
          setCurrentTrack(trackToRestart);
          setIsPlaying(true);
        });
        forceRepaint();
      });
    });
  }, [activeTrack, actedUponIds, setCurrentTrack, setIsPlaying, forceRepaint]);
  
  // Long-press handlers for Next/Restart button
  const handleNextButtonTouchStart = useCallback((e) => {
    e.preventDefault();
    setIsLongPress(false);
    
    // Start long-press timer (500ms)
    longPressTimerRef.current = setTimeout(() => {
      setIsLongPress(true);
      handleRestart();
      // Haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 500);
  }, [handleRestart]);
  
  const handleNextButtonTouchEnd = useCallback((e) => {
    e.preventDefault();
    
    // Clear long-press timer
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    
    // If it wasn't a long press, treat as Next
    if (!isLongPress) {
      handleNext(e);
    }
    
    setIsLongPress(false);
  }, [isLongPress, handleNext]);
  
  const handleNextButtonTouchCancel = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setIsLongPress(false);
  }, []);
  
  // ============================================================
  // SPOT BUTTON - Move track TO Spotlight (FINAL action)
  // ============================================================
  const handleSpot = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const trackId = activeTrack?.id;
    
    // Guard: Check if track exists and hasn't been acted upon
    if (!trackId || actedUponIds.has(trackId) || !addToSpotlight) {
      console.log('[LiveMobile] Spot blocked - no track or already acted upon');
      return;
    }
    
    console.log('[LiveMobile] Spot tapped - FINAL action on track:', trackId);
    
    // 1. Mark as acted upon FIRST (prevents double-counting)
    markAsActedUpon(trackId);
    
    // 2. Call API to add to spotlight
    addToSpotlight(trackId);
    
    // 3. Advance to next track after short delay
    setTimeout(advanceToNextTrack, 300);
    
  }, [activeTrack, actedUponIds, addToSpotlight, markAsActedUpon, advanceToNextTrack]);
  
  // ============================================================
  // LIST BUTTON (Blue) - Toggle shuffled Spotlight playlist
  // Works independently of Upcoming queue
  // 
  // SOUNDCLOUD AUTOPLAY FIX:
  // SoundCloud requires audio to be "unlocked" in the same call stack as
  // the user gesture. We use a two-phase approach:
  // Phase 1 (here): Call unlockAudio() synchronously, then set pendingPlaylistStart
  // Phase 2 (useEffect): Actual playback state is set, triggering player component
  // ============================================================
  const handleList = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('[LiveMobile] List tapped, playlistMode:', localPlaylistMode);
    console.log('[LiveMobile] Spotlight tracks available:', spotlightTracks.length);
    
    // Get available spotlight tracks (excluding acted-upon)
    const availableSpotlight = spotlightTracks;
    
    if (availableSpotlight.length === 0) {
      console.log('[LiveMobile] No spotlight tracks available');
      return;
    }
    
    // === PHASE 1: Unlock audio SYNCHRONOUSLY in user gesture context ===
    // This is CRITICAL for SoundCloud/iOS Safari autoplay
    // The unlockAudio() call must happen in the same call stack as the click
    console.log('[LiveMobile] Phase 1: Unlocking audio...');
    unlockAudio();
    
    if (localPlaylistMode && localIsPlaying) {
      // In List mode and playing - PAUSE
      console.log('[LiveMobile] Pausing List');
      setLocalIsPlaying(false);
      flushSync(() => {
        setIsPlaying(false);
      });
      forceRepaint();
    } else if (localPlaylistMode && !localIsPlaying) {
      // In List mode but paused - RESUME
      console.log('[LiveMobile] Resuming List');
      setLocalIsPlaying(true);
      flushSync(() => {
        setIsPlaying(true);
      });
      forceRepaint();
    } else {
      // Not in List mode - START shuffled playlist
      console.log('[LiveMobile] Starting shuffled List with', availableSpotlight.length, 'tracks');
      
      const shuffled = [...availableSpotlight].sort(() => Math.random() - 0.5);
      
      console.log('[LiveMobile] First track:', shuffled[0]?.title);
      console.log('[LiveMobile] First track platform:', shuffled[0]?.platform);
      
      // Check if first track is SoundCloud - use two-phase approach
      const isSoundCloud = shuffled[0]?.platform === 'soundcloud';
      
      if (isSoundCloud) {
        // === SoundCloud: Use two-phase approach ===
        // Phase 1 complete (unlockAudio called above)
        // Phase 2 will be handled by useEffect
        console.log('[LiveMobile] SoundCloud track detected - using two-phase approach');
        
        // Update local state immediately for UI feedback
        setLocalPlaylistMode(true);
        setLocalTrack(shuffled[0]);
        setLocalIsPlaying(true);
        
        // Trigger Phase 2 via state update
        setPendingPlaylistStart({ tracks: shuffled, timestamp: Date.now() });
        
        forceRepaint();
      } else {
        // === Non-SoundCloud: Direct playback (Spotify/YouTube embed handles autoplay) ===
        console.log('[LiveMobile] Non-SoundCloud track - direct playback');
        
        // Update local state
        setLocalPlaylistMode(true);
        setLocalTrack(shuffled[0]);
        setLocalIsPlaying(true);
        
        // Update parent state directly
        flushSync(() => {
          setPlaylistQueue(shuffled);
          setPlaylistIndex(0);
          setPlaylistMode(true);
          setCurrentTrack(shuffled[0]);
          setIsPlaying(true);
        });
        
        forceRepaint();
      }
    }
  }, [localPlaylistMode, localIsPlaying, spotlightTracks, setIsPlaying, 
      setCurrentTrack, setPlaylistMode, setPlaylistQueue, setPlaylistIndex, forceRepaint]);
  
  // ============================================================
  // DONE BUTTON - Move to Finished (FINAL action)
  // ============================================================
  const handleFinish = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const trackId = activeTrack?.id;
    
    // Guard: Check if track exists and hasn't been acted upon
    if (!trackId || actedUponIds.has(trackId) || !moveToFinished) {
      console.log('[LiveMobile] Done blocked - no track or already acted upon');
      return;
    }
    
    console.log('[LiveMobile] Done tapped - FINAL action on track:', trackId);
    
    // 1. Mark as acted upon FIRST (prevents double-counting)
    markAsActedUpon(trackId);
    
    // 2. Increment counter
    setFinishedCount(c => c + 1);
    
    // 3. Call API to move to finished
    moveToFinished(trackId);
    
    // 4. Advance to next track after short delay
    setTimeout(advanceToNextTrack, 300);
    
  }, [activeTrack, actedUponIds, moveToFinished, markAsActedUpon, advanceToNextTrack]);
  
  // ============================================================
  // DEL BUTTON - Move to Removed (FINAL action)
  // Works in both Upcoming and List modes
  // ============================================================
  const handleRemove = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const trackId = activeTrack?.id;
    
    // Guard: Check if track exists and hasn't been acted upon
    if (!trackId || actedUponIds.has(trackId) || !moveToRemoved) {
      console.log('[LiveMobile] Del blocked - no track or already acted upon');
      return;
    }
    
    console.log('[LiveMobile] Del tapped - FINAL action on track:', trackId, 'playlistMode:', localPlaylistMode);
    
    // 1. Mark as acted upon FIRST (prevents double-counting)
    markAsActedUpon(trackId);
    
    // 2. Increment counter
    setRemovedCount(c => c + 1);
    
    // 3. Call API to move to removed
    moveToRemoved(trackId);
    
    // 4. Advance to next track after short delay
    setTimeout(() => {
      if (localPlaylistMode && playlistQueue && playlistQueue.length > 0) {
        // In List mode - advance in playlist (skip the removed track)
        const nextIndex = playlistIndex + 1;
        // Find next track that hasn't been acted upon
        let foundNext = false;
        for (let i = nextIndex; i < playlistQueue.length; i++) {
          const nextTrack = playlistQueue[i];
          if (nextTrack && !actedUponIds.has(nextTrack.id) && nextTrack.id !== trackId) {
            console.log('[LiveMobile] Advancing to next in playlist:', nextTrack.title);
            unlockAudio();
            setLocalTrack(nextTrack);
            setLocalIsPlaying(true);
            flushSync(() => {
              setPlaylistIndex(i);
              setCurrentTrack(nextTrack);
              setIsPlaying(true);
            });
            foundNext = true;
            break;
          }
        }
        
        if (!foundNext) {
          // No more tracks in playlist - exit List mode
          console.log('[LiveMobile] End of playlist, exiting List mode');
          setLocalPlaylistMode(false);
          setLocalTrack(null);
          setLocalIsPlaying(false);
          flushSync(() => {
            setPlaylistMode(false);
            setPlaylistQueue([]);
            setPlaylistIndex(0);
            setCurrentTrack(null);
            setIsPlaying(false);
          });
        }
      } else {
        // In Upcoming mode - advance to next Upcoming track
        advanceToNextTrack();
      }
      forceRepaint();
    }, 300);
    
  }, [activeTrack, actedUponIds, moveToRemoved, markAsActedUpon, localPlaylistMode, 
      playlistQueue, playlistIndex, advanceToNextTrack, setCurrentTrack, setIsPlaying, 
      setPlaylistMode, setPlaylistQueue, setPlaylistIndex, forceRepaint]);
  
  // Default background video (served from backend API)
  const DEFAULT_BACKGROUND_VIDEO = '/api/live-mobile/default-background';
  
  // Background media - use custom if set, otherwise use the default test pattern video
  // EITHER/OR: Image takes priority over video when set
  const hasCustomImage = backgroundsLoaded && (customBackgroundImage || reviewer?.live_mobile_image);
  const hasCustomVideo = backgroundsLoaded && (customBackgroundVideo || reviewer?.live_mobile_video);
  
  // If user has a custom image, use it (no video)
  // If user has a custom video, use it
  // Otherwise, use default video
  const backgroundImage = hasCustomImage ? (customBackgroundImage || reviewer?.live_mobile_image) : null;
  const backgroundVideo = hasCustomImage 
    ? null  // Don't show video if image is set
    : (hasCustomVideo 
        ? (customBackgroundVideo || reviewer?.live_mobile_video)
        : DEFAULT_BACKGROUND_VIDEO);
  
  // Handle background update from settings modal
  const handleBackgroundUpdate = useCallback((updates) => {
    if (updates.image !== undefined) {
      setCustomBackgroundImage(updates.image);
    }
    if (updates.video !== undefined) {
      setCustomBackgroundVideo(updates.video);
    }
  }, []);
  
  // Track info - show active track or "waiting" message
  const trackInfo = activeTrack 
    ? `${activeTrack.artist || 'Unknown Artist'} - ${activeTrack.title || activeTrack.track_title || 'Untitled'}`
    : 'Waiting for tracks...';

  // Check if we have a valid track to act upon
  const hasTrack = !!activeTrack && !actedUponIds.has(activeTrack.id);
  // eslint-disable-next-line no-unused-vars
  const _hasAnyTracks = upcomingTracks.length > 0 || spotlightTracks.length > 0;

  return (
    <div 
      ref={containerRef}
      className="live-mobile-container" 
      data-testid="live-mobile-view"
      data-render-key={renderKey}
    >
      {/* Settings Modal */}
      <LiveMobileSettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        currentImage={customBackgroundImage || reviewer?.live_mobile_image}
        currentVideo={customBackgroundVideo || reviewer?.live_mobile_video}
        onBackgroundUpdate={handleBackgroundUpdate}
      />
      
      {/* Branding Banner - Full width at top */}
      <div className="live-mobile-branding" data-testid="live-mobile-branding">
        <div className="live-mobile-branding-gold-bar">
          {/* Back Button - Inside gold bar, left side */}
          <button 
            className="live-mobile-close"
            onClick={onClose}
            data-testid="live-mobile-close"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          {/* Connection Status Indicator */}
          <span 
            className={`live-mobile-connection-indicator ${wsConnected ? 'connected' : 'disconnected'}`}
            title={wsConnected ? 'Connected' : 'Reconnecting...'}
            data-testid="live-mobile-connection-status"
          >
            <span className="live-mobile-connection-dot" />
          </span>
          
          <span className="live-mobile-branding-title">MIC CHECK MILLIONAIRE</span>
          
          {/* Help Button - Shows button guide dropdown */}
          <div className="live-mobile-help-container">
            <button 
              className="live-mobile-help-btn"
              onClick={() => setShowHelpMenu(!showHelpMenu)}
              data-testid="live-mobile-help-btn"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
            
            {showHelpMenu && (
              <div className="live-mobile-help-dropdown" data-testid="live-mobile-help-dropdown">
                <div className="help-dropdown-header">
                  <span>Button Guide</span>
                  <button onClick={() => setShowHelpMenu(false)} className="help-close-btn">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="help-dropdown-content">
                  <div className="help-item">
                    <div className="help-icon play"><Play className="w-4 h-4" /></div>
                    <div className="help-text">
                      <strong>Play/Pause</strong>
                      <span>Play or pause the current track</span>
                    </div>
                  </div>
                  <div className="help-item">
                    <div className="help-icon next"><SkipForward className="w-4 h-4" /></div>
                    <div className="help-text">
                      <strong>Next</strong>
                      <span>Skip to next track. Long-press to restart current track</span>
                    </div>
                  </div>
                  <div className="help-item">
                    <div className="help-icon spot"><Star className="w-4 h-4" /></div>
                    <div className="help-text">
                      <strong>Spot</strong>
                      <span>Add track to Spotlight (your favorites)</span>
                    </div>
                  </div>
                  <div className="help-item">
                    <div className="help-icon list"><List className="w-4 h-4" /></div>
                    <div className="help-text">
                      <strong>List</strong>
                      <span>Auto-play all Spotlight tracks in sequence</span>
                    </div>
                  </div>
                  <div className="help-item">
                    <div className="help-icon done"><Check className="w-4 h-4" /></div>
                    <div className="help-text">
                      <strong>Done</strong>
                      <span>Mark track as reviewed and move to finished</span>
                    </div>
                  </div>
                  <div className="help-item">
                    <div className="help-icon del"><Trash2 className="w-4 h-4" /></div>
                    <div className="help-text">
                      <strong>Del</strong>
                      <span>Remove track from queue (skip review)</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Settings Button - Inside gold bar, right side */}
          <button 
            className="live-mobile-settings"
            onClick={() => setShowSettings(true)}
            data-testid="live-mobile-settings-btn"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
        <span className="live-mobile-branding-subtitle">LIVE MUSIC REVIEWS</span>
      </div>
      
      <div className="live-mobile-background">
        {backgroundVideo ? (
          <video
            ref={videoRef}
            key={backgroundVideo}
            src={backgroundVideo}
            className="live-mobile-video"
            playsInline
            muted
            loop
            autoPlay
            preload="auto"
            onCanPlay={() => {
              console.log('✅ Video can play:', backgroundVideo);
              if (videoRef.current) {
                videoRef.current.play().catch(err => {
                  console.log('Autoplay blocked:', err.message);
                });
              }
            }}
            onError={(e) => {
              const error = e.target?.error;
              console.error('Video loading issue:', error?.code, error?.message || 'Unknown');
              // Don't clear the video - keep trying. User can manually change in settings
            }}
          />
        ) : (
          <div 
            className="live-mobile-image"
            style={{ 
              backgroundImage: backgroundImage 
                ? `url(${backgroundImage})` 
                : 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)'
            }}
          />
        )}
        <div className="live-mobile-gradient" />
      </div>
      
      {!hasTrack && (
        <div className="live-mobile-empty-overlay">
          <div className="live-mobile-empty-content">
            <span className="text-4xl mb-4">🎵</span>
            <h3 className="text-xl font-bold text-white mb-2">Waiting for submissions...</h3>
            <p className="text-gray-400 text-sm">Share your session link to receive tracks</p>
          </div>
        </div>
      )}
      
      {skipNotification && (
        <div className={`live-mobile-skip-toast ${
          skipNotification.type === 'custom' ? 'toast-custom' :
          skipNotification.type === 'superskip' ? 'toast-superskip' : 'toast-skip'
        }`}>
          <span className="text-2xl">{skipNotification.emoji}</span>
          <div>
            <p className="font-bold">NEW {skipNotification.label}!</p>
            <p className="text-sm opacity-80">{skipNotification.title}</p>
          </div>
        </div>
      )}
      
      <div className="live-mobile-controls">
        {/* Username - Positioned above track row */}
        <div className="live-mobile-username-row">
          <div className="live-mobile-username">
            @{reviewer?.username || reviewer?.name || 'Reviewer'}
          </div>
        </div>
        
        <div className="live-mobile-track-row">
          <div className="live-mobile-track-container">
            <div className="live-mobile-track-marquee">
              <span className="live-mobile-track-text">
                🎵 {trackInfo} <span className="live-mobile-cta">• Submit Music @ miccheckmillionaire.com 🔗</span>
              </span>
              <span className="live-mobile-track-text" aria-hidden="true">
                🎵 {trackInfo} <span className="live-mobile-cta">• Submit Music @ miccheckmillionaire.com 🔗</span>
              </span>
            </div>
          </div>
          
          <div className="live-mobile-badges-row">
            {upcomingCount > 0 && (
              <div className="live-mobile-badge live-mobile-badge-new">
                <Bell className="w-4 h-4" />
                <span>{upcomingCount}</span>
              </div>
            )}
            {paidCount > 0 && (
              <div className="live-mobile-badge live-mobile-badge-paid">
                <span>🤑</span>
                <span>{paidCount}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="live-mobile-buttons">
          <div className="live-mobile-single-row">
            {/* PLAY button with counter */}
            {/* Only for Upcoming queue - disabled if no upcoming tracks */}
            <button 
              className={`live-mobile-btn-unified ${localIsPlaying && !localPlaylistMode ? 'btn-unified-active' : ''}`}
              onClick={handlePlay}
              disabled={upcomingCount === 0}
              data-testid="live-mobile-play"
            >
              {localIsPlaying && !localPlaylistMode ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              <span>{localIsPlaying && !localPlaylistMode ? 'Pause' : 'Play'}</span>
              {upcomingCount > 0 && (
                <span className="live-mobile-btn-counter live-mobile-btn-counter-play">{upcomingCount}</span>
              )}
            </button>
            
            {/* NEXT button (tap) / RESTART (long-press) */}
            {/* Tap: advance to next track | Long-press (500ms): restart current track */}
            <button 
              className={`live-mobile-btn-unified ${isLongPress ? 'btn-unified-active' : ''}`}
              onTouchStart={handleNextButtonTouchStart}
              onTouchEnd={handleNextButtonTouchEnd}
              onTouchCancel={handleNextButtonTouchCancel}
              onClick={(e) => e.preventDefault()} // Prevent click on touch devices
              onMouseDown={(e) => { // Desktop fallback
                if (e.button === 0) handleNextButtonTouchStart(e);
              }}
              onMouseUp={(e) => {
                if (e.button === 0) handleNextButtonTouchEnd(e);
              }}
              onMouseLeave={handleNextButtonTouchCancel}
              disabled={!hasTrack || !localIsPlaying}
              data-testid="live-mobile-next"
              title="Tap: Next track | Hold: Restart"
            >
              <SkipForward className="w-5 h-5" />
              <span>{isLongPress ? 'Restart' : 'Next'}</span>
            </button>
            
            {/* SPOT button - Add to Spotlight (FINAL) */}
            {/* Disabled when in List mode (track is already spotlighted) */}
            <button 
              className="live-mobile-btn-unified btn-unified-spotlight"
              onClick={handleSpot}
              disabled={!hasTrack || localPlaylistMode}
              data-testid="live-mobile-spot"
            >
              <Star className="w-5 h-5" />
              <span>Spot</span>
            </button>
            
            {/* LIST button (Blue) - Toggle Spotlight playlist */}
            <button 
              className={`live-mobile-btn-unified btn-unified-list ${localPlaylistMode ? 'btn-unified-active' : ''}`}
              onClick={handleList}
              disabled={spotlightTracks.length === 0}
              data-testid="live-mobile-list"
            >
              {localPlaylistMode && localIsPlaying ? <Pause className="w-5 h-5" /> : <List className="w-5 h-5" />}
              <span>{localPlaylistMode ? (localIsPlaying ? 'Pause' : 'Resume') : 'List'}</span>
            </button>
            
            {/* DONE button with counter (FINAL) */}
            {/* Disabled when in List mode (Spotlight = already reviewed, prevent double-counting) */}
            <button 
              className="live-mobile-btn-unified btn-unified-done"
              onClick={handleFinish}
              disabled={!hasTrack || localPlaylistMode}
              data-testid="live-mobile-done"
            >
              <Check className="w-5 h-5" />
              <span>Done</span>
              {finishedCount > 0 && (
                <span className="live-mobile-btn-counter">{finishedCount}</span>
              )}
            </button>
            
            {/* DEL button with counter (FINAL) */}
            {/* Works in both Upcoming and List modes - can always remove a track */}
            <button 
              className="live-mobile-btn-unified btn-unified-remove"
              onClick={handleRemove}
              disabled={!hasTrack}
              data-testid="live-mobile-remove"
            >
              <Trash2 className="w-5 h-5" />
              <span>Del</span>
              {removedCount > 0 && (
                <span className="live-mobile-btn-counter">{removedCount}</span>
              )}
            </button>
          </div>
        </div>
        
        {children && (
          <div className="live-mobile-player">
            {children}
          </div>
        )}
        
        <div className="live-mobile-queue-info">
          {upcomingCount} in queue • {spotlightTracks.length} in spotlight
          {localIsPlaying && (localPlaylistMode ? ' • Playing List' : ' • Now playing')}
        </div>
      </div>
    </div>
  );
};

export default LiveMobileView;
